/**
 * 쇼핑몰 Route
 * --
 */
import React, { useState, useEffect } from 'react';
import { MainLayout, Receipt } from 'components/Layout';
import {
  Route,
  Routes,
  useNavigate,
  useLocation,
  useSearchParams,
  useParams,
} from 'react-router-dom';
import { Button, ConfigProvider, Spin } from 'antd';
import {
  DaumAddress,
  Payment,
  PaymentResult,
  Result,
  SocialLogin,
  AuthFail,
  AuthSuccess,
  Auth,
  DefaultDataProvider,
  UserDataProvider,
  RegisterCardPopup,
} from 'components';

// Route
import Main from './Main';
import SignIn from './SignIn';
import SignUp from './SignUp';
import FindStore from './FindStore';
import GradeBenefits from './GradeBenefits';
import {
  FindAccount,
  FindPassword,
  FindAccountResult,
  FindPasswordResult,
} from './Find';
import Products from './Products';
import ProductDetail from './ProductDetail';
import {
  MypageHome,
  MypageOrder,
  MypageOrderDetails,
  MypageSubscribe,
  MypageSubscribeDetails,
  MypageRate,
  MypageCoupon,
  MypageWishlist,
  MypageRecentlySaw,
  MypageQnA,
  MypageRestock,
  MypagePoint,
  MypageCancel,
  MypagePay,
  MypageEvent,
  MypageEditProfile,
  MypageAddress,
  MypageReview,
  MypageReviewForm,
  MypagePresents,
} from './Mypage';
import {
  HelpQuestions,
  HelpInquire,
  HelpNotice,
  HelpNoticeDetails,
  HelpInquireForm,
} from './Help';
import { CouponEventHome, CouponEventDetails } from './CouponEvent';
import { Cart, Confirmed, Order } from './Order';
import Admin from './Admin';
import { Map } from 'routes/mobile.routes';
import { Privacy } from './Policy';
import Brand from './Brand';
import BrandDetail from './BrandDetail';
// import { WithdrawAccount, WithdrawConfirm } from './Withdraw';
import Withdraw from './Withdraw';
import { useUserAction, useUserDataAction, useUserStack } from 'store';
import { AddressPopup } from './Order/Order.Order/components';
import { getCookie } from 'utils';
import JmpayPopup from './Mypage/components/JmpayPopup';
import { Member, Present, PresentDetail } from './Common';
import ApplyContainer from 'routes/admin.routes/admin.apply';
import { PromotionHome, PromotionDetail } from './Promotion';

const MainRoutes = () => {
  /* ===== ROUTE ===== */
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams();

  /* ===== Store ===== */
  const userInfo = useUserStack();
  const userAction = useUserAction();
  const userDataAction = useUserDataAction();

  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [screenSize, setScreenSize] = useState(document.body.clientWidth);
  const [loading, setLoading] = React.useState(false);

  /* ===== Variable ===== */
  /**
   * Antd Provider 설정
   * --
   */
  const theme = {
    token: {
      colorPrimary: `var(--black-color)`,
      borderRadius: 0,
      colorBorder: `var(--dark-gray-color)`,
    },
    components: {
      Button: {
        defaultBorderColor: `var(--dark-gray-color)`,
      },
      Input: {
        colorBorder: `var(--dark-gray-color)`,
      },
      Select: {
        optionSelectedBg: 'rgba(0, 0, 0, 0.1)',
        colorBorder: `#DDDDDD`,
        activeOutlineColor: 'var(--dark-gray-color)',
      },
      Radio: {
        radioSize: 24,
        dotSize: 8,
      },
      Checkbox: {
        colorBorder: '#DDDDDD',
      },
      Badge: {
        textFontSize: 10,
        indicatorHeight: 15,
        colorError: '#D33444',
      },
      DatePicker: {
        cellActiveWithRangeBg: '#F2F2F2',
      },
    },
  };

  const defaultProps = {
    navigate: navigate,
    location: location,
    searchParams: searchParams,
    params: params,
    screenSize: screenSize,
  };

  /* ===== FUNCTION ===== */

  /**
   * 라우트 권한 설정
   * --
   * @param {Object} props
   * @returns
   */
  const PrivateCheck = ({ children }) => {
    const oat = getCookie('JM_COFFEE_ACCESS_TOKEN');

    if (!userInfo) {
      window.alert('로그인 후 이용해주세요!');
      navigate('/signin', { state: { from: location.pathname } });
      return;
    }

    if (!oat) {
      window.alert('세션이 만료되었습니다!\n다시 로그인해주세요');
      userAction.resetUser();
      userDataAction.resetAllData();

      navigate('/signin', { state: { from: location.pathname } });
      return;
    }

    return children;
  };

  /* ===== HOOKS ===== */
  /**
   * 스크린 사이즈 변화 감지
   * --
   */
  useEffect(() => {
    const call = (e) => {
      setScreenSize(document.body.clientWidth);
    };

    window.addEventListener('resize', call);

    return () => {
      window.removeEventListener('resize', call);
    };
  }, []);

  /**
   * 로딩 라우팅 처리
   * --
   * @param {*} param0
   * @returns
   */
  const LoadingRoute = ({ children }) => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [percent, setPercent] = React.useState(-50);
    const timerRef = React.useRef(null);

    React.useEffect(() => {
      timerRef.current = setTimeout(() => {
        if (percent !== 100) {
          setPercent((v) => {
            const nextPercent = v + 5;
            return nextPercent;
          });
        } else {
          clearTimeout(timerRef.current);
          setIsLoading(false);
        }
      }, 100);

      return () => clearTimeout(timerRef.current);
    }, [percent]);

    return (
      <Spin spinning={isLoading} percent={percent}>
        {children}
      </Spin>
    );
  };

  /* ===== RENDER ===== */
  return (
    <ConfigProvider theme={theme}>
      <DefaultDataProvider>
        <UserDataProvider user={userInfo}>
          <Spin spinning={loading}>
            <MainLayout
              headerExceptPath={['admin', 'components', 'social', 'common']}
              footerExceptPath={['admin', 'components', 'social', 'common']}
              siderExceptPath={[
                'signin',
                'signup',
                'find',
                'components',
                'social',
                'common',
                'admin',
              ]}
              topBannerExceptPath={['admin', 'components', 'social', 'common']}
              {...defaultProps}
              setLoading={setLoading}
            >
              <Routes>
                <Route path="main" element={<Main {...defaultProps} />} />
                <Route path="signin" element={<SignIn {...defaultProps} />} />
                <Route path="signup">
                  <Route index element={<SignUp {...defaultProps} />} />
                  <Route
                    path="success"
                    element={
                      <Result
                        {...defaultProps}
                        title={'회원가입되었습니다'}
                        extra={
                          <Button
                            size="large"
                            onClick={() => navigate(`/signin`)}
                          >
                            로그인
                          </Button>
                        }
                      />
                    }
                  />
                </Route>
                <Route path="find">
                  <Route path="account">
                    <Route index element={<FindAccount {...defaultProps} />} />
                    <Route
                      path="result"
                      element={<FindAccountResult {...defaultProps} />}
                    />
                  </Route>
                  <Route path="password">
                    <Route index element={<FindPassword {...defaultProps} />} />
                    <Route
                      path="result"
                      element={<FindPasswordResult {...defaultProps} />}
                    />
                  </Route>
                </Route>
                <Route path="products">
                  <Route index element={<Products {...defaultProps} />} />
                  <Route
                    path=":productId"
                    element={<ProductDetail {...defaultProps} />}
                  />
                </Route>
                <Route path="mypage">
                  <Route
                    index
                    path="home"
                    element={
                      <PrivateCheck>
                        <MypageHome {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                  <Route path="order">
                    <Route
                      index
                      element={
                        <PrivateCheck>
                          <MypageOrder {...defaultProps} />
                        </PrivateCheck>
                      }
                    />
                    <Route
                      index
                      path=":orderId"
                      element={
                        <PrivateCheck>
                          <MypageOrderDetails {...defaultProps} />
                        </PrivateCheck>
                      }
                    />
                  </Route>
                  <Route path="subscribe">
                    <Route
                      index
                      element={
                        <PrivateCheck>
                          <MypageSubscribe {...defaultProps} />
                        </PrivateCheck>
                      }
                    />
                    <Route
                      index
                      path=":orderId"
                      element={
                        <PrivateCheck>
                          <MypageSubscribeDetails {...defaultProps} />
                        </PrivateCheck>
                      }
                    />
                  </Route>

                  <Route
                    index
                    path="rate"
                    element={
                      <PrivateCheck>
                        <MypageRate {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                  <Route
                    index
                    path="coupon"
                    element={
                      <PrivateCheck>
                        <MypageCoupon {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                  <Route path="wishlist">
                    <Route
                      path="wishlist"
                      element={
                        <PrivateCheck>
                          <MypageWishlist {...defaultProps} />
                        </PrivateCheck>
                      }
                    />
                    <Route
                      path="recentlysaw"
                      element={<MypageRecentlySaw {...defaultProps} />}
                    />
                  </Route>
                  <Route
                    path="qna"
                    element={
                      <PrivateCheck>
                        <MypageQnA {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                  <Route
                    path="presents"
                    element={<MypagePresents {...defaultProps} />}
                  />
                  <Route
                    path="restock"
                    element={<MypageRestock {...defaultProps} />}
                  />
                  <Route
                    path="point"
                    element={
                      <PrivateCheck>
                        <MypagePoint {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                  <Route
                    path="cancel"
                    element={
                      <PrivateCheck>
                        <MypageCancel {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                  <Route
                    path="point"
                    element={
                      <PrivateCheck>
                        <MypagePoint {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                  <Route
                    path="pay"
                    element={
                      <PrivateCheck>
                        <MypagePay {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                  <Route
                    path="event"
                    element={<MypageEvent {...defaultProps} />}
                  />
                  <Route
                    path="editprofile"
                    element={
                      <PrivateCheck>
                        <MypageEditProfile {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                  <Route
                    path="address"
                    element={
                      <PrivateCheck>
                        <MypageAddress {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                  <Route
                    path="review"
                    element={
                      <PrivateCheck>
                        <MypageReview {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                  <Route
                    path="reviewform"
                    element={
                      <PrivateCheck>
                        <MypageReviewForm {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                </Route>

                {/* 고객센터 */}
                <Route path="cs">
                  <Route
                    path="faq"
                    element={<HelpQuestions {...defaultProps} />}
                  />
                  <Route path="notice">
                    <Route index element={<HelpNotice {...defaultProps} />} />
                    <Route
                      path=":noticeId"
                      element={<HelpNoticeDetails {...defaultProps} />}
                    />
                  </Route>
                  <Route
                    path="inquire"
                    element={<HelpInquire {...defaultProps} />}
                  />
                  <Route
                    path="inquireform"
                    element={
                      <PrivateCheck>
                        <HelpInquireForm {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                </Route>

                {/* 쿠폰&이벤트 */}
                <Route path="events">
                  <Route
                    index
                    element={<CouponEventHome {...defaultProps} />}
                  />
                  <Route
                    path=":eventId"
                    element={<CouponEventDetails {...defaultProps} />}
                  />
                </Route>

                {/* 주문 */}
                <Route path="order">
                  <Route
                    path="cart"
                    element={
                      <PrivateCheck>
                        <Cart {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                  <Route
                    path="order"
                    element={
                      <PrivateCheck>
                        <Order {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                  <Route
                    path="confirmed"
                    element={
                      <PrivateCheck>
                        <Confirmed {...defaultProps} />
                      </PrivateCheck>
                    }
                  />
                </Route>

                {/* 브랜드 메인 */}
                <Route path="brand">
                  <Route index element={<Brand {...defaultProps} />} />
                  <Route
                    path=":brandId"
                    element={<BrandDetail {...defaultProps} />}
                  />
                </Route>
                {/* 브랜드 상세 */}

                {/* 프로모션 메인 */}
                <Route path="promotion">
                  <Route
                    index
                    element={
                      <PromotionHome type={'promotion'} {...defaultProps} />
                    }
                  />
                  <Route
                    path=":promotionId"
                    element={
                      <PromotionDetail type={'promotion'} {...defaultProps} />
                    }
                  />
                </Route>
                {/* 프로모션 상세 */}

                {/* 레시피 메인 */}
                <Route path="recipe">
                  <Route
                    index
                    element={
                      <PromotionHome type={'recipe'} {...defaultProps} />
                    }
                  />
                  <Route
                    path=":promotionId"
                    element={
                      <PromotionDetail type={'recipe'} {...defaultProps} />
                    }
                  />
                </Route>
                {/* 레시피 상세 */}

                {/* 정책 */}
                <Route path="policy">
                  <Route
                    path=":policyType"
                    element={<Privacy {...defaultProps} />}
                  />
                </Route>

                {/* 정책 */}
                {/* <Route path="policy">
                <Route path="privacy" element={<Privacy {...defaultProps} />} />
              </Route> */}

                {/* 회원탈퇴 */}
                <Route
                  path="withdraw"
                  element={<Withdraw {...defaultProps} />}
                />

                {/* 매장찾기 */}
                <Route
                  path="findstore"
                  element={<FindStore {...defaultProps} />}
                />

                {/* 등급혜택 */}
                <Route
                  path="gradebenefits"
                  element={<GradeBenefits {...defaultProps} />}
                />

                {/* 관리자 로그인 */}
                <Route path="admin" element={<Admin />} />

                {/* 웹뷰 컴포넌트 */}
                <Route path="components">
                  <Route path="signin" element={<SignIn {...defaultProps} />} />
                  <Route path="signup">
                    <Route index element={<SignUp {...defaultProps} />} />
                    <Route
                      path="success"
                      element={
                        <Result
                          {...defaultProps}
                          title={'회원가입되었습니다'}
                          subTitle={'앱으로 돌아가 로그인해주세요.'}
                          extra={
                            <Button
                              onClick={() =>
                                (window.location.href = 'jmcoffee://signin')
                              }
                            >
                              돌아가기
                            </Button>
                          }
                        />
                      }
                    />
                  </Route>
                  <Route
                    path="payment"
                    element={<Payment {...defaultProps} />}
                  />
                  <Route
                    path="paymentResult"
                    element={<PaymentResult {...defaultProps} />}
                  />
                  <Route path="maps" element={<Map {...defaultProps} />} />
                  <Route path="find">
                    <Route path="account">
                      <Route
                        index
                        element={<FindAccount {...defaultProps} />}
                      />
                      <Route
                        path="result"
                        element={<FindAccountResult {...defaultProps} />}
                      />
                    </Route>
                    <Route path="password">
                      <Route
                        index
                        element={<FindPassword {...defaultProps} />}
                      />
                      <Route
                        path="result"
                        element={<FindPasswordResult {...defaultProps} />}
                      />
                    </Route>
                  </Route>

                  <Route
                    path="daumAddress"
                    element={<DaumAddress {...defaultProps} />}
                  />
                </Route>

                <Route path="common">
                  <Route path="order">
                    <Route
                      path="receipt/:order_id"
                      element={<Receipt {...defaultProps} />}
                    />
                  </Route>
                  <Route
                    path="delivery-address"
                    element={<AddressPopup {...defaultProps} />}
                  />

                  <Route path="card">
                    <Route
                      path="register"
                      element={<RegisterCardPopup {...defaultProps} />}
                    />
                  </Route>

                  <Route path="jmpay">
                    <Route
                      path="charging"
                      element={<JmpayPopup {...defaultProps} />}
                    />
                  </Route>

                  <Route path="member" element={<Member {...defaultProps} />} />
                  <Route path="present">
                    <Route index element={<Present {...defaultProps} />} />
                    <Route
                      path=":presentId"
                      element={<PresentDetail {...defaultProps} />}
                    />
                  </Route>

                  <Route
                    path="apply"
                    element={
                      <LoadingRoute>
                        <ApplyContainer />
                      </LoadingRoute>
                    }
                  />

                  {/* 등급혜택 */}
                  <Route
                    path="gradebenefits"
                    element={<GradeBenefits {...defaultProps} />}
                  />
                </Route>

                {/* 본인인증 */}
                <Route path="auth/*">
                  <Route index element={<Auth />} />
                  <Route path="success" element={<AuthSuccess />} />
                  <Route path="fail" element={<AuthFail />} />
                </Route>

                {/* 소셜로그인 */}
                <Route
                  path="social"
                  element={<SocialLogin {...defaultProps} />}
                />
              </Routes>
            </MainLayout>
          </Spin>
        </UserDataProvider>
      </DefaultDataProvider>
    </ConfigProvider>
  );
};

export default MainRoutes;
